import { iterateNodeElements } from "@imminently/decisively-core";
import { find } from "lodash";

export type GraphNode = {
  id?: string;
  path?: string;
  hidden?: boolean;
  description?: string;
  reference?: unknown;
  links?: unknown[];
  derived?: unknown;
  input?: unknown;
  value?: unknown;
};

export type ReportTreeNode = {
  type?: "special";
  title?: string;
  node?: GraphNode;
  hidden?: boolean;
  description?: string;
  reference?: unknown;
  links?: unknown[];
  expanded?: boolean;
  children?: ReportTreeNode[];
};

export type GraphLike = {
  node: (id: string) => GraphNode | undefined;
  successors: (id: string) => string[];
  _nodes?: unknown;
};

const ruleDetailKeys = new Set(["conditions", "otherwise", "rows", "columns", "value"]);

/** Strips rule-authoring detail (conditions/rows/etc) from a report tree, matching the "Download" (non-rules) export. */
export function stripRuleDetailFields(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(stripRuleDetailFields);
  }

  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([key]) => !ruleDetailKeys.has(key))
        .map(([key, item]) => [key, stripRuleDetailFields(item)]),
    );
  }

  return value;
}

/**
 * The successors of `id` in decision report order: the order the node's rule references them, grouped by instance
 * path, with duplicates removed. Shared by the report and the HTML card `sessionGraph` data, so both list children the
 * same way.
 */
export function orderedSuccessors(graph: GraphLike, id: string): string[] {
  const node = graph.node(id);
  const successors = [...(graph.successors(id) ?? [])];
  if (!node || successors.length === 0) return successors;
  const elementIds = new Set<string>();
  iterateNodeElements(node as any, (element: any): undefined => {
    if (element?.attributeId) {
      elementIds.add(element.attributeId);
    }
    return undefined;
  });

  /**
   * the ids are pathed and look like the following:
   *
   * pet/1/<guid 1>
   * pet/1/<guid 2>
   * pet/1/leash/1/<guid 1>
   * pet/1/leash/2/<guid 1>
   * pet/1/leash/3/<guid 1>
   * pet/1/leash/4/<guid 1>
   * pet/2/<guid 1>
   * pet/2/<guid 2>
   * pet/2/leash/1/<guid 1>
   * pet/2/leash/2/<guid 1>
   * pet/3/<guid 1>
   * pet/3/<guid 2>
   *
   * we need to sort by the pathing first and then by the ordering in elementIds
   */

  const elementIdArray = Array.from(elementIds);

  // Group by top-level path part, then by the order found in elementIds
  successors.sort((a: string, b: string) => {
    // Rule definition order is the primary order when both successors
    // correspond to attributes referenced by this rule.
    const aNodeId = graph.node(a)?.id;
    const bNodeId = graph.node(b)?.id;
    const idxA = elementIdArray.indexOf(aNodeId || a);
    const idxB = elementIdArray.indexOf(bNodeId || b);
    if (idxA !== -1 && idxB !== -1 && idxA !== idxB) return idxA - idxB;

    // get path segments
    const segA = a.split("/");
    const segB = b.split("/");

    const aPath = segA.slice(0, -1).join("/");
    const bPath = segB.slice(0, -1).join("/");

    if (aPath !== bPath) {
      // Group by base entity (e.g. pet/1 vs pet/1/leash/2, etc)
      const aPathParts = aPath.split("/");
      const bPathParts = bPath.split("/");
      for (let i = 0; i < Math.max(aPathParts.length, bPathParts.length); i++) {
        if (aPathParts[i] !== bPathParts[i]) {
          // Compare by path segment (numbers as numbers, others as string)
          const numA = Number(aPathParts[i]);
          const numB = Number(bPathParts[i]);
          if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
            return numA - numB;
          } else {
            return (aPathParts[i] || "").localeCompare(bPathParts[i] || "");
          }
        }
      }
    }

    // If paths are entirely equal, fall back to rule definition order.
    // Successors may be pathed instance ids, while rule elements store raw attribute ids.
    if (idxA !== -1 && idxB !== -1) return idxA - idxB;
    if (idxA !== -1) return -1;
    if (idxB !== -1) return 1;
    return 0;
  });

  const seenSuccessors = new Set<string>();
  const uniqueSuccessors = successors.filter((child: string) => {
    const childNode = graph.node(child);
    const key = childNode?.path || childNode?.id || child;
    if (seenSuccessors.has(key)) return false;
    seenSuccessors.add(key);
    return true;
  });
  return uniqueSuccessors;
}

/**
 * Walks a rule-dependency graph (dagre graphlib.Graph or equivalent) from `id`, producing the
 * goal-rooted derivation tree consumed by the standalone Decision Lens timeline explorer
 * (docs/prototype/template.html) and rendered by DecisionReport.tsx. Nodes already visited on
 * the current path are collapsed into a `{ type: "special", title: "Proven elsewhere" }` cross-edge
 * rather than re-descended, to keep cyclic/shared derivations from producing an infinite tree.
 */
export function recurseDecisionReportNode(
  graph: GraphLike,
  id: string,
  seen: string[] = [],
): ReportTreeNode | undefined {
  const node = graph.node(id);
  if (!node || node.hidden === true) return undefined;
  if (seen.indexOf(id) > -1) {
    const children = graph.successors(id);
    if (!children || children.length === 0) return { node, children: [] };
    return {
      node,
      children: [
        {
          type: "special",
          title: "Proven elsewhere",
          node,
        },
      ],
    };
  }
  seen.push(id);

  const children = orderedSuccessors(graph, id)
    .map((child: string) => recurseDecisionReportNode(graph, child, seen))
    .filter((x): x is ReportTreeNode => Boolean(x));

  return {
    node,
    title: node.description,
    expanded: true,
    children,
  };
}

/**
 * Builds the goal-rooted decision report tree (as an array with a single root, matching the shape
 * DecisionReport.tsx downloads and template.html's ingestReport() expects) for a given graph + goal.
 */
export function buildDecisionReportTree(
  graph: GraphLike,
  goal: string,
  { includeRules = false }: { includeRules?: boolean } = {},
): ReportTreeNode[] {
  if (!goal || !graph) return [];
  let goalNode = graph.node(goal);
  if (goalNode?.hidden) {
    // `goal` isn't always a raw attribute id by the time it gets here — VisualisationsPanel.tsx
    // pre-resolves it to `goalNode.path || goalNode.id` before calling in. A hidden node is a
    // template/model shared across instances, and its `.id` field and the string it was looked up
    // by (a path, in that case) are different things. Searching for a visible sibling with
    // `find(graph._nodes, { id: goal, ... })` therefore searched for a node whose `.id` equals a
    // *path* string, which never matches anything, silently returning no sibling every time.
    // Use the found node's own `.id` instead, matching the working pattern in
    // getRuleGraphFocusOptions (InvestigationsTab.tsx), which resolves the same "hidden
    // template -> visible instance" case correctly.
    const instanceModelId = goalNode.id;
    goalNode = find(graph._nodes as any, { id: instanceModelId, hidden: false });
  }

  const tree = [recurseDecisionReportNode(graph, goalNode?.path ?? goal)].filter((x): x is ReportTreeNode =>
    Boolean(x),
  );
  return includeRules ? tree : (stripRuleDetailFields(tree) as ReportTreeNode[]);
}
